/**
 * VenueEntryPage.tsx — 1904va
 * Replaces AuthPage as the default entry point.
 *
 * Detection order:
 *   1. Geolocation → auto-join if one event in range
 *   2. Geolocation → show picker if multiple events in range
 *   3. Code input fallback
 *   4. Manual event list
 *
 * After joining → role select → straight into The Room
 * Contact capture happens as a soft prompt later (not here)
 */
import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  MapPin, Keyboard, ChevronRight, Loader2,
  Radio, Users, Camera, Newspaper, Briefcase, CheckSquare
} from 'lucide-react'
import {
  fetchActiveEvents,
  detectNearbyEvents,
  matchEventByCode,
  joinEventAnonymously,
  type EventWithEntry,
} from '@/lib/venueEntry'
import { useAuthStore } from '@/stores/authStore'
import { ensureRecipient, acceptTos } from '@/lib/supabase'
import { cn } from '@/components/ui/utils'
import type { ParticipantRole } from '@/types'

type Step = 'detecting' | 'pick-event' | 'enter-code' | 'pick-role' | 'joining' | 'error'

const ROLES: { value: ParticipantRole; label: string; desc: string; Icon: typeof Camera }[] = [
  { value: 'creator',   label: 'Creator',   desc: 'Photographer / videographer', Icon: Camera    },
  { value: 'press',     label: 'Press',      desc: 'Journalist / media',          Icon: Newspaper },
  { value: 'organiser', label: 'Organiser',  desc: 'Event team / production',     Icon: Radio     },
  { value: 'sponsor',   label: 'Sponsor',    desc: 'Brand / commercial',          Icon: Briefcase },
  { value: 'publisher', label: 'Publisher',  desc: 'Content platform',            Icon: Users     },
]

export default function VenueEntryPage() {
  const navigate = useNavigate()
  const { setParticipant, user, participant } = useAuthStore()

  // Already have a valid session — go straight in
  useEffect(() => {
    if (user && participant) {
      navigate('/upload', { replace: true })
    }
  }, [user, participant, navigate])

  const [step,         setStep]         = useState<Step>('detecting')
  const [nearbyEvents, setNearbyEvents] = useState<EventWithEntry[]>([])
  const [_allEvents,    setAllEvents]    = useState<EventWithEntry[]>([])
  const [selectedEvent, setSelectedEvent] = useState<EventWithEntry | null>(null)
  const [code,         setCode]         = useState('')
  const [codeError,    setCodeError]    = useState('')
  const [role,         setRole]         = useState<ParticipantRole>('creator')
  const [tosAccepted,  setTosAccepted]  = useState(false)
  const [errorMsg,     setErrorMsg]     = useState('')
  const [geoStatus,    setGeoStatus]    = useState<'checking' | 'found' | 'none'>('checking')

  // ── Step 1: detect on mount ──────────────────────────────────────────────────
  useEffect(() => {
    async function detect() {
      try {
        const all = await fetchActiveEvents()
        setAllEvents(all)

        const { events: nearby, method } = await detectNearbyEvents()
        setGeoStatus(method === 'geo' ? 'found' : 'none')

        if (nearby.length === 1) {
          // One event in range — auto-select, just pick role
          setSelectedEvent(nearby[0])
          setStep('pick-role')
        } else if (nearby.length > 1) {
          // Multiple — show picker
          setNearbyEvents(nearby)
          setStep('pick-event')
        } else {
          // Not detected nearby — show all events to pick from
          setNearbyEvents(all)
          setStep('pick-event')
        }
      } catch {
        const all = await fetchActiveEvents().catch(() => [])
        setAllEvents(all)
        setNearbyEvents(all)
        setGeoStatus('none')
        setStep('pick-event')
      }
    }
    detect()
  }, [])

  // ── Code submit ──────────────────────────────────────────────────────────────
  async function handleCodeSubmit() {
    if (!code.trim()) { setCodeError('Enter an event code'); return }
    setCodeError('')
    const event = await matchEventByCode(code)
    if (!event) { setCodeError('Code not recognised — check with your organiser'); return }
    setSelectedEvent(event)
    setStep('pick-role')
  }

  // ── Join ─────────────────────────────────────────────────────────────────────
  async function handleJoin() {
    if (!selectedEvent) return
    if (!tosAccepted) return
    setStep('joining')
    try {
      const entryMethod = geoStatus === 'found' ? 'geo' : code ? 'code' : 'manual'
      const participant = await joinEventAnonymously(selectedEvent.id, role, entryMethod)
      await ensureRecipient(participant.id, [], ['photo', 'video', 'audio', 'document'])
      await acceptTos(participant.id)
      setParticipant(participant)
      navigate('/upload', { replace: true })
    } catch (err) {
      setErrorMsg((err as Error).message ?? 'Could not join. Try again.')
      setStep('error')
    }
  }

  // ── Existing account link ────────────────────────────────────────────────────
  function goToMagicLink() {
    navigate('/auth')
  }

  // ────────────────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-room-bg text-white flex flex-col">
      {/* Ambient */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[500px] h-[500px] bg-room-accent/8 rounded-full blur-[120px]" />
        <div className="absolute bottom-0 right-0 w-64 h-64 bg-purple-900/10 rounded-full blur-3xl" />
      </div>

      <div className="relative flex-1 flex flex-col max-w-sm mx-auto w-full px-6 py-10">
        {/* Logo */}
        <div className="text-center mb-8">
          <h1 className="text-5xl font-black tracking-tight">
            <span className="bg-gradient-to-br from-purple-300 via-room-accent to-purple-500 bg-clip-text text-transparent">
              The Room
            </span>
          </h1>
          <p className="text-neutral-500 text-sm mt-2">If you're not in The Room, you're missing it.</p>
        </div>

        {/* ── Detecting ── */}
        {step === 'detecting' && (
          <div className="flex-1 flex flex-col items-center justify-center gap-5">
            <div className="w-16 h-16 rounded-2xl bg-room-surface border border-room-border flex items-center justify-center">
              <Loader2 size={28} className="animate-spin text-room-accent" />
            </div>
            <div className="text-center">
              <p className="text-white font-semibold">Finding your event…</p>
              <p className="text-neutral-500 text-sm mt-1">Checking your location</p>
            </div>
          </div>
        )}

        {/* ── Pick event ── */}
        {step === 'pick-event' && (
          <div className="flex-1 flex flex-col gap-5">
            <div>
              {geoStatus === 'found'
                ? <p className="text-white font-semibold">Events near you</p>
                : <p className="text-white font-semibold">Select your event</p>
              }
              <p className="text-neutral-500 text-sm mt-0.5">
                {geoStatus === 'found' ? 'You\'re on location' : 'Tap the event you\'re attending'}
              </p>
            </div>

            <div className="space-y-2 flex-1">
              {nearbyEvents.length === 0 ? (
                <div className="text-center py-10 text-neutral-500">
                  <MapPin size={32} className="mx-auto mb-3 opacity-30" />
                  <p className="text-sm">No active events found</p>
                </div>
              ) : (
                nearbyEvents.map(ev => (
                  <button
                    key={ev.id}
                    onClick={() => { setSelectedEvent(ev); setStep('pick-role') }}
                    className="w-full flex items-center gap-4 px-4 py-4 rounded-2xl border border-room-border bg-room-surface hover:border-room-accent/60 transition-all text-left group"
                  >
                    <div
                      className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                      style={{ backgroundColor: (ev.sponsor_color ?? '#d946ef') + '22' }}
                    >
                      <Radio size={18} style={{ color: ev.sponsor_color ?? '#d946ef' }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-semibold text-sm truncate">{ev.name}</p>
                      {ev.venue && <p className="text-neutral-500 text-xs truncate">{ev.venue}</p>}
                      {ev.prize_draw_enabled && (
                        <p className="text-room-gold text-xs mt-0.5">🏆 {ev.prize_draw_label}</p>
                      )}
                    </div>
                    <ChevronRight size={16} className="text-neutral-600 group-hover:text-room-accent transition-colors shrink-0" />
                  </button>
                ))
              )}
            </div>

            {/* Code fallback */}
            <button
              onClick={() => setStep('enter-code')}
              className="flex items-center justify-center gap-2 py-3 text-sm text-neutral-500 hover:text-neutral-300 transition-colors"
            >
              <Keyboard size={14} />
              Have an event code?
            </button>
          </div>
        )}

        {/* ── Enter code ── */}
        {step === 'enter-code' && (
          <div className="flex-1 flex flex-col gap-5">
            <div>
              <p className="text-white font-semibold">Enter event code</p>
              <p className="text-neutral-500 text-sm mt-0.5">Your organiser will have given you this</p>
            </div>

            <input
              autoFocus
              value={code}
              onChange={e => { setCode(e.target.value.toUpperCase()); setCodeError('') }}
              onKeyDown={e => e.key === 'Enter' && handleCodeSubmit()}
              placeholder="e.g. IBIZA26"
              className={cn(
                'w-full bg-room-surface border rounded-2xl px-5 py-4 text-white text-xl font-bold tracking-[0.3em] text-center placeholder-neutral-700',
                'focus:outline-none focus:ring-2 focus:ring-room-accent focus:border-transparent transition-all',
                codeError ? 'border-room-error' : 'border-room-border'
              )}
              maxLength={12}
            />
            {codeError && <p className="text-room-error text-sm text-center">{codeError}</p>}

            <button
              onClick={handleCodeSubmit}
              className="w-full py-4 rounded-2xl bg-room-accent hover:bg-fuchsia-600 text-white font-bold text-base transition-colors"
            >
              Continue
            </button>

            <button
              onClick={() => setStep('pick-event')}
              className="text-center text-sm text-neutral-500 hover:text-neutral-300 transition-colors"
            >
              ← Back to events
            </button>
          </div>
        )}

        {/* ── Pick role ── */}
        {step === 'pick-role' && selectedEvent && (
          <div className="flex-1 flex flex-col gap-5">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <div
                  className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: selectedEvent.sponsor_color ?? '#d946ef' }}
                />
                <p className="text-neutral-400 text-sm">{selectedEvent.name}</p>
              </div>
              <p className="text-white font-semibold text-lg">What's your role?</p>
            </div>

            <div className="space-y-2 flex-1">
              {ROLES.map(({ value, label, desc, Icon }) => (
                <button
                  key={value}
                  onClick={() => setRole(value)}
                  className={cn(
                    'w-full flex items-center gap-4 px-4 py-3.5 rounded-2xl border text-left transition-all',
                    role === value
                      ? 'border-room-accent bg-room-accent/10'
                      : 'border-room-border bg-room-surface hover:border-neutral-600'
                  )}
                >
                  <div className={cn(
                    'w-9 h-9 rounded-xl flex items-center justify-center shrink-0 transition-colors',
                    role === value ? 'bg-room-accent/20' : 'bg-neutral-800'
                  )}>
                    <Icon size={16} className={role === value ? 'text-room-accent' : 'text-neutral-400'} />
                  </div>
                  <div>
                    <p className={cn('text-sm font-semibold', role === value ? 'text-white' : 'text-neutral-300')}>{label}</p>
                    <p className="text-xs text-neutral-500">{desc}</p>
                  </div>
                {role === value && (
                    <span className="ml-auto text-room-accent text-sm">✓</span>
                  )}
                </button>
              ))}
            </div>

            {/* ToS acceptance — large tap target, white text, nightclub legible */}
            <button
              onClick={() => setTosAccepted(v => !v)}
              className="w-full flex items-start gap-3 py-3 text-left"
            >
              <div className={cn(
                'mt-0.5 w-6 h-6 rounded-md border-2 flex items-center justify-center shrink-0 transition-all',
                tosAccepted
                  ? 'bg-room-accent border-room-accent'
                  : 'border-white/40 bg-transparent'
              )}>
                {tosAccepted && <CheckSquare size={14} className="text-white" />}
              </div>
              <p className="text-sm text-white leading-snug">
                I agree to the{' '}
                <a
                  href="/terms"
                  onClick={e => e.stopPropagation()}
                  className="underline text-room-accent"
                >Terms of Service</a>
                {' '}and{' '}
                <a
                  href="/privacy"
                  onClick={e => e.stopPropagation()}
                  className="underline text-room-accent"
                >Privacy Notice</a>
              </p>
            </button>

            <button
              onClick={handleJoin}
              disabled={!tosAccepted}
              className={cn(
                'w-full py-4 rounded-2xl text-white font-bold text-base transition-all',
                tosAccepted
                  ? 'bg-room-accent hover:bg-fuchsia-600'
                  : 'bg-room-accent/30 cursor-not-allowed'
              )}
            >
              Enter The Room →
            </button>

            <p className="text-center text-xs text-room-muted/60">
              Founder access — everything at no cost while we build this together
            </p>

            {selectedEvent.prize_draw_enabled && (
              <p className="text-center text-xs text-room-gold">
                You'll be entered into the {selectedEvent.prize_draw_label} after joining
              </p>
            )}
          </div>
        )}

        {/* ── Joining ── */}
        {step === 'joining' && (
          <div className="flex-1 flex flex-col items-center justify-center gap-5">
            <div className="w-16 h-16 rounded-2xl bg-room-surface border border-room-border flex items-center justify-center">
              <Loader2 size={28} className="animate-spin text-room-accent" />
            </div>
            <p className="text-white font-semibold">Entering The Room…</p>
          </div>
        )}

        {/* ── Error ── */}
        {step === 'error' && (
          <div className="flex-1 flex flex-col items-center justify-center gap-5 text-center">
            <p className="text-white font-semibold">Something went wrong</p>
            <p className="text-neutral-400 text-sm">{errorMsg}</p>
            <button
              onClick={() => setStep('detecting')}
              className="px-6 py-3 rounded-xl bg-room-accent text-white font-semibold text-sm"
            >
              Try again
            </button>
          </div>
        )}

        {/* ── Footer: existing account ── */}
        {(step === 'pick-event' || step === 'enter-code' || step === 'pick-role') && (
          <button
            onClick={goToMagicLink}
            className="text-center text-xs text-neutral-600 hover:text-neutral-400 transition-colors mt-4"
          >
            Already have an account? Sign in
          </button>
        )}
      </div>
    </div>
  )
}
